/**
 * TestimonialsAdmin — Manage client testimonials (empty slots + real reviews).
 *
 * Rules (per PRD): do NOT invent fake reviews. Always start with empty state.
 * Admin can create, edit, publish/unpublish, delete testimonials.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Save, X, Eye, EyeOff, Star, MessageSquareQuote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyTestimonial = () => ({
  author_name: '',
  author_role: '',
  content: '',
  rating: 5,
  project_type: '',
  published: false,
  order: 0,
});

const StarRow = ({ rating, onChange, size = 16 }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={onChange ? () => onChange(n) : undefined}
        className={`${onChange ? 'cursor-pointer' : 'cursor-default'} focus:outline-none`}
      >
        <Star
          size={size}
          className={`${n <= rating ? 'text-[#d9fb06] fill-[#d9fb06]' : 'text-[#3f4816]'} transition-colors`}
        />
      </button>
    ))}
  </div>
);

export default function TestimonialsAdmin({ token }) {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // {mode: 'create'|'edit', data: {...}}
  const [saving, setSaving] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/testimonials`, { headers: authHeaders });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTestimonials(data.testimonials || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      load();
    }
  }, [token]);

  const startCreate = () => setEditing({ mode: 'create', data: emptyTestimonial() });
  const startEdit = (t) => setEditing({ mode: 'edit', data: { ...t } });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const { mode, data } = editing;
    if (!data.author_name?.trim() || !data.content?.trim() || data.content.trim().length < 5) {
      setError('Nom et contenu (min 5 caractères) obligatoires');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const url = mode === 'create'
        ? `${API_URL}/api/admin/testimonials`
        : `${API_URL}/api/admin/testimonials/${data.id}`;
      const method = mode === 'create' ? 'POST' : 'PATCH';
      const payload = { ...data };
      // Only send changed fields on PATCH — but backend uses exclude_unset so it's fine
      const res = await fetch(url, { method, headers: authHeaders, body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (t) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/testimonials/${t.id}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ published: !t.published }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer l'avis de "${t.author_name}" ?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/testimonials/${t.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked
      : e.target.type === 'number' ? parseInt(e.target.value || '0', 10) : e.target.value;
    setEditing((cur) => ({ ...cur, data: { ...cur.data, [k]: v } }));
  };

  const published = testimonials.filter((t) => t.published);
  const drafts = testimonials.filter((t) => !t.published);

  return (
    <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-6 mb-8" data-testid="testimonials-admin">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquareQuote className="text-[#d9fb06]" size={22} />
          <h2 className="text-xl font-black text-[#d9fb06] uppercase">Avis clients</h2>
          <span className="text-[#888680] text-xs ml-2" data-testid="testimonials-count">
            {published.length} publié(s) · {drafts.length} brouillon(s)
          </span>
        </div>
        {!editing && (
          <button
            onClick={startCreate}
            className="flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] px-4 py-2 rounded-full font-bold text-sm hover:bg-[#c4e205] transition-colors"
            data-testid="new-testimonial-btn"
          >
            <Plus size={16} /> Nouvel avis
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300 mb-4">
          {error}
        </div>
      )}

      {/* Edit / Create form */}
      {editing && (
        <div className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg p-4 mb-4" data-testid="testimonial-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-[#888680] mb-1 uppercase">Nom du client *</label>
              <input value={editing.data.author_name} onChange={set('author_name')}
                className="w-full bg-[#302f2c] border border-[#3f4816] rounded px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                data-testid="testimonial-input-name" placeholder="Ex : Julien D." maxLength={120} />
            </div>
            <div>
              <label className="block text-xs text-[#888680] mb-1 uppercase">Rôle / Contexte</label>
              <input value={editing.data.author_role || ''} onChange={set('author_role')}
                className="w-full bg-[#302f2c] border border-[#3f4816] rounded px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                data-testid="testimonial-input-role" placeholder="Ex : Acheteur voilier · La Rochelle" maxLength={160} />
            </div>
          </div>
          <div className="mb-3">
            <label className="block text-xs text-[#888680] mb-1 uppercase">Message *</label>
            <textarea value={editing.data.content} onChange={set('content')} rows={4} maxLength={2000}
              className="w-full bg-[#302f2c] border border-[#3f4816] rounded px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
              data-testid="testimonial-input-content" placeholder="L'avis du client…" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs text-[#888680] mb-1 uppercase">Note</label>
              <StarRow rating={editing.data.rating || 5}
                onChange={(n) => setEditing((cur) => ({ ...cur, data: { ...cur.data, rating: n } }))} />
            </div>
            <div>
              <label className="block text-xs text-[#888680] mb-1 uppercase">Prestation</label>
              <select value={editing.data.project_type || ''} onChange={set('project_type')}
                className="w-full bg-[#302f2c] border border-[#3f4816] rounded px-2 py-1.5 text-sm text-[#dfddd6]">
                <option value="">—</option>
                <option value="analyse">Analyse d&apos;annonce</option>
                <option value="audit">Audit pré-achat</option>
                <option value="recherche">Recherche bateau</option>
                <option value="negociation">Négociation</option>
                <option value="pack">Pack complet</option>
                <option value="coaching">Coaching primo</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#888680] mb-1 uppercase">Ordre</label>
              <input type="number" value={editing.data.order || 0} onChange={set('order')}
                className="w-full bg-[#302f2c] border border-[#3f4816] rounded px-3 py-1.5 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
                data-testid="testimonial-input-order" />
            </div>
            <label className="flex items-center gap-2 self-end cursor-pointer">
              <input type="checkbox" checked={!!editing.data.published} onChange={set('published')}
                className="accent-[#d9fb06]" data-testid="testimonial-input-published" />
              <span className="text-sm text-[#dfddd6]">Publier</span>
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] px-4 py-2 rounded-full text-sm font-bold hover:bg-[#c4e205] disabled:opacity-50"
              data-testid="testimonial-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editing.mode === 'create' ? 'Créer' : 'Enregistrer'}
            </button>
            <button onClick={cancel}
              className="flex items-center gap-2 border border-[#888680] text-[#888680] px-4 py-2 rounded-full text-sm hover:text-[#dfddd6] hover:border-[#dfddd6]">
              <X size={14} /> Annuler
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && !editing && testimonials.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-[#3f4816] rounded-lg" data-testid="testimonials-empty">
          <MessageSquareQuote className="mx-auto text-[#3f4816] mb-3" size={48} />
          <p className="text-[#dfddd6] font-bold mb-1">Aucun avis client pour l&apos;instant.</p>
          <p className="text-[#888680] text-sm mb-4">
            Ajoutez ici les retours de vos clients dès qu&apos;ils vous en envoient.<br />
            <span className="italic">Aucun avis n&apos;est inventé — c&apos;est vous qui les créez.</span>
          </p>
          <button onClick={startCreate}
            className="inline-flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] px-4 py-2 rounded-full font-bold text-sm hover:bg-[#c4e205]">
            <Plus size={16} /> Ajouter le premier avis
          </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-6">
          <Loader2 className="mx-auto animate-spin text-[#d9fb06]" size={24} />
        </div>
      )}

      {/* List */}
      {!loading && testimonials.length > 0 && (
        <div className="space-y-2" data-testid="testimonials-list">
          {testimonials.map((t) => (
            <div
              key={t.id}
              className={`flex items-start justify-between gap-3 border rounded-lg p-3 ${
                t.published ? 'bg-[#1a1c1b] border-[#3f4816]' : 'bg-[#1a1c1b] border-[#3f4816]/50 opacity-70'
              }`}
              data-testid={`testimonial-row-${t.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[#dfddd6] font-bold">{t.author_name}</span>
                  {t.author_role && <span className="text-[#888680] text-xs">— {t.author_role}</span>}
                  <StarRow rating={t.rating || 5} size={12} />
                  {!t.published && <span className="text-[10px] uppercase bg-[#3f4816] text-[#888680] px-2 py-0.5 rounded">Brouillon</span>}
                  {t.project_type && <span className="text-[10px] uppercase bg-[#d9fb06]/10 text-[#d9fb06] px-2 py-0.5 rounded">{t.project_type}</span>}
                </div>
                <p className="text-[#dfddd6] text-sm line-clamp-2">{t.content}</p>
              </div>
              <div className="flex flex-shrink-0 gap-1">
                <button onClick={() => togglePublished(t)} title={t.published ? 'Dépublier' : 'Publier'}
                  className="p-2 text-[#888680] hover:text-[#d9fb06]">
                  {t.published ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button onClick={() => startEdit(t)}
                  className="bg-[#d9fb06] text-[#1a1c1b] px-3 py-1.5 rounded-full text-xs font-bold hover:bg-[#c4e205]">
                  Éditer
                </button>
                <button onClick={() => remove(t)} title="Supprimer"
                  className="p-2 text-[#888680] hover:text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
